import { Link } from "react-router-dom";
import { ArrowUpRight, Compass, Eye, Target } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { SectionHeading } from "@/components/site/SectionHeading";
import { Reveal } from "@/components/site/Reveal";
import { Timeline } from "@/components/site/Timeline";
import { CtaBand } from "@/components/site/CtaBand";
import { IMAGES } from "@/data/content";

const chapters = [
  {
    no: "01",
    title: "Who We Are",
    body:
      "SPMS Global Limited is an international sourcing and trade partner. We help businesses find reliable suppliers, source suitable products and move goods across markets — acting as a single, accountable point of contact throughout the supply chain.",
  },
  {
    no: "02",
    title: "What We Do",
    body:
      "Our work spans import, export, global sourcing, procurement and supply, logistics, warehousing, quality inspection and complete trade support. We coordinate the moving parts so our clients can focus on their business.",
  },
  {
    no: "03",
    title: "Our Approach",
    body:
      "We operate on four principles — reliability, quality, transparency and long-term relationships. Every engagement is inspection-led, clearly communicated and built to earn repeat business.",
  },
];

const About = () => {
  return (
    <>
      <PageHero
        eyebrow="About Us"
        title="Built to make global trade simpler and more dependable."
        subtitle="We combine sourcing expertise, quality discipline and coordinated logistics into one reliable partnership."
        image={IMAGES.handshakeDeal}
      />

      {/* Numbered manifesto chapters */}
      <section className="bg-white py-24 lg:py-32" data-testid="about-chapters">
        <div className="container-x grid gap-16 lg:grid-cols-[1fr_1.15fr] lg:gap-24">
          <div className="lg:sticky lg:top-28 lg:self-start">
            <SectionHeading
              eyebrow="Our Story"
              title="A partner you can build a business around."
              subtitle="No exaggerated claims — just a disciplined, transparent way of working across international trade."
            />
            <Reveal delay={0.1}>
              <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white">
                <img src="https://static.prod-images.emergentagent.com/jobs/035dfac8-8c03-4209-8998-c754ab9897e7/images/8518712af3f64dbe447249730d49407f5033cb63dadbf7f8f2c13f683b4d3bea.jpeg" alt="International trade flow diagram with a global sourcing and delivery cycle" className="h-64 w-full object-contain p-4" />
              </div>
            </Reveal>
          </div>

          <div className="space-y-12">
            {chapters.map((c, i) => (
              <Reveal key={c.no} delay={i * 0.08}>
                <div className="border-t border-slate-200 pt-8" data-testid={`chapter-${c.no}`}>
                  <div className="flex items-baseline gap-5">
                    <span className="font-[Outfit] text-4xl font-extrabold text-[color:var(--brand-blue)]/25">{c.no}</span>
                    <div>
                      <h3 className="text-2xl font-bold text-slate-950">{c.title}</h3>
                      <p className="mt-3 text-base leading-relaxed text-slate-600">{c.body}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="bg-[#fafafa] py-24 lg:py-32" data-testid="mission-vision">
        <div className="container-x grid gap-6 md:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-9">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[color:var(--brand-red)]/10 text-[color:var(--brand-red)]">
                <Target className="h-6 w-6" strokeWidth={1.6} />
              </div>
              <h3 className="mt-6 text-2xl font-bold text-slate-950">Our Mission</h3>
              <p className="mt-3 text-base leading-relaxed text-slate-600">
                To make international sourcing and trade reliable and transparent for every client —
                connecting businesses with the right products and suppliers, and delivering them
                dependably, end to end.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-2xl border border-slate-200 bg-white p-9">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[color:var(--brand-blue)]/10 text-[color:var(--brand-blue)]">
                <Eye className="h-6 w-6" strokeWidth={1.6} />
              </div>
              <h3 className="mt-6 text-2xl font-bold text-slate-950">Our Vision</h3>
              <p className="mt-3 text-base leading-relaxed text-slate-600">
                To become a trusted global trade and sourcing partner — known for quality, integrity
                and long-term relationships across the markets we serve.
              </p>
            </div>
          </Reveal>
        </div>

        <div className="container-x mt-16">
          <SectionHeading eyebrow="How We Work" title="Our process, end to end" />
          <div className="mt-14">
            <Timeline />
          </div>
        </div>
      </section>

      <CtaBand />
    </>
  );
};

export default About;
